/**
 * Paywall utilities: when to show upgrade/expire UI for team plans.
 *
 * Paywall is shown to OWNER, ADMIN, and MEMBER when the team is considered "expired".
 * - Web — Owner/Admin: "Upgrade to Team Plan" CTA (full checkout flow)
 * - Native — Owner/Admin: "Learn More" CTA (opens upgrade guide URL; payment is web-only)
 * - Member (web and native): "Notify Owner" CTA (they cannot upgrade directly)
 *
 * Scenarios that trigger paywall (team treated as expired):
 * 1. Subscription canceled + no payment card on file (paymentCardLast4 null/undefined).
 * 2. Trial ended (expirationDate in the past) + no payment card on file.
 * 3. Subscription inactive (e.g. not active, or status !== 'active') and has expirationDate.
 * 4. Subscription has expirationDate in the past (current time > expirationDate).
 *
 * Paywall is NOT shown when:
 * - subscriptionDetails.expirationDate exists and is still in the future (access valid until then).
 * - Team is personal.
 */

import dayjs from 'dayjs';

import { Team, TeamMemberRole } from '@graphql/generated';

/** Minimal team shape for paywall (query fragments may omit full Team). */
export type TeamPaywallShape = {
  personal?: boolean;
  hasReachedMaxProjects?: boolean;
  subscriptionDetails?: Team['subscriptionDetails'];
  currentUserRole?: TeamMemberRole | string | null;
};

function hasNoPaymentCard(paymentCardLast4?: string | null): boolean {
  return paymentCardLast4 === null || paymentCardLast4 === undefined;
}

// function isTrialEndedForTeam(team?: TeamPaywallShape | null): boolean {
//   const expirationDate = team?.subscriptionDetails?.expirationDate;
//   if (!expirationDate) return false;
//   return dayjs().isAfter(dayjs(expirationDate));
// }

function isExpirationDateInFuture(
  expirationDate?: string | number | Date | null
): boolean {
  if (!expirationDate) return false;
  return dayjs().isBefore(dayjs(expirationDate));
}

/** Minimal shape for trial eligibility check (listTeams items). */
type TeamWithSubscriptionShape = {
  subscriptionDetails?: unknown;
  personal?: boolean;
  currentUserRole?: TeamMemberRole | string | null;
};

/**
 * Returns true when the user is eligible for a free trial on their personal team.
 *
 * Requirements:
 * - currentTeamData must be personal with no subscriptionDetails.
 * - User is NOT eligible if any team in allTeams has subscription details AND the
 *   user is owner of that team (subscription history on teams the user owned).
 * - The backend rejects trials for users who have ever had a subscription on a team they owned.
 */
export function isUserTrialEligible(
  allTeams: (TeamWithSubscriptionShape | null | undefined)[],
  currentTeamData?: TeamPaywallShape | null
): boolean {
  if (!currentTeamData?.personal) return false;
  if (currentTeamData?.subscriptionDetails) return false;
  if (currentTeamData?.currentUserRole !== TeamMemberRole.Owner) return false; // ADD THIS
  if (allTeams.length === 0) return false;

  const hasSubscriptionHistory = allTeams.some(
    (t) =>
      t?.subscriptionDetails != null &&
      t.subscriptionDetails !== undefined &&
      t.currentUserRole === TeamMemberRole.Owner
  );
  return !hasSubscriptionHistory;
}

/**
 * Returns true when a personal team has reached its project limit (at capacity).
 * Use this for paywall gating only — not for "expired" messaging.
 */
export function hasReachedPersonalProjectLimit(
  team?: Team | TeamPaywallShape | null
): boolean {
  if (!team?.personal) return false;
  return team.hasReachedMaxProjects ?? false;
}

/**
 * Returns true when the team subscription is expired (see file-level comment for scenarios).
 * Used for expire banners and subscription-expiry UI.
 * For personal teams, always returns false — use hasReachedPersonalProjectLimit for capacity checks.
 */
export function isTeamExpired(team?: Team | TeamPaywallShape | null): boolean {
  if (!team) return false;
  if (team.personal) {
    return false; // Personal teams don't "expire" — use hasReachedPersonalProjectLimit for creation gating
  }

  const subscriptionDetails = team.subscriptionDetails;
  if (!subscriptionDetails) return false;

  const paymentCardLast4 = subscriptionDetails.paymentCardLast4;
  const expirationDate = subscriptionDetails.expirationDate;

  if (isExpirationDateInFuture(expirationDate)) {
    return false;
  }

  if (
    subscriptionDetails.isSubscriptionCanceled &&
    hasNoPaymentCard(paymentCardLast4)
  ) {
    return true;
  }

  if (expirationDate && hasNoPaymentCard(paymentCardLast4)) {
    return true;
  }

  const isActive =
    subscriptionDetails.isSubscriptionActive ||
    subscriptionDetails.subscriptionStatus === 'active';

  if (expirationDate) {
    const isExpired = dayjs().isAfter(dayjs(expirationDate));
    if (!isActive || isExpired) {
      return true;
    }
  }

  return false;
}

/**
 * Returns true when paywall should be shown: team subscription is expired.
 * Shown to all team roles — TeamPaywallModal adapts its CTA by role.
 * For personal teams this always returns false — use hasReachedPersonalProjectLimit for creation gating.
 */
export function shouldShowPaywall(
  team?: Team | TeamPaywallShape | null
): boolean {
  if (!team) return false;
  if (!isTeamExpired(team)) return false;
  return true;
}

/** Minimal project shape for paywall checks. */
export type ProjectPaywallShape = {
  team?: Team | TeamPaywallShape | null;
};

/**
 * Same as shouldShowPaywall but takes a project; uses project.team.
 */
export function shouldShowPaywallForProject(
  project?: ProjectPaywallShape | null
): boolean {
  if (!project?.team) return false;
  return shouldShowPaywall(project.team);
}
